import React, { useState, useEffect, useCallback } from 'react';
import { useDebounce } from '../../hooks/useDebounce';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  alpha,
  useTheme,
  Skeleton,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  Tooltip,
  Avatar,
} from '@mui/material';
import {
  Add,
  Search,
  CheckCircle,
  Cancel,
  EventBusy,
  Undo,
  EditCalendarRounded,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { format } from 'date-fns';
import leaveService, { LeaveRequest } from '../../services/leaveService';
import ConfirmationDialog from '../../components/common/ConfirmationDialog';
import RevokeLeaveDialog from '../../components/leave/RevokeLeaveDialog';
import EditLeaveDatesDialog from '../../components/leave/EditLeaveDatesDialog';

const LeaveApprovalPage: React.FC = () => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  // State
  const [loading, setLoading] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);
  const [leaveRequests, setLeaveRequests] = useState<LeaveRequest[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const [statusFilter, setStatusFilter] = useState<string>(''); // Default to All
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Rejection dialog
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectingRequest, setRejectingRequest] = useState<LeaveRequest | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');

  // Approval dialog
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [approvingRequest, setApprovingRequest] = useState<LeaveRequest | null>(null);

  // Revoke + edit dialogs — admin actions on APPROVED rows.
  const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);
  const [revokingRequest, setRevokingRequest] = useState<LeaveRequest | null>(null);
  const [revoking, setRevoking] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingRequest, setEditingRequest] = useState<LeaveRequest | null>(null);
  const [editing, setEditing] = useState(false);

  // Fetch data with server-side pagination
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data, pagination } = await leaveService.getLeaveRequestsForApproval({
        status: statusFilter || undefined,
        search: debouncedSearchTerm || undefined,
        page,
        limit: rowsPerPage,
      });
      setLeaveRequests(data || []);
      setTotalCount(pagination?.total || 0);
    } catch (error: any) {
      enqueueSnackbar(error.message || 'Failed to load leave requests', { variant: 'error' });
    } finally {
      setLoading(false);
      setInitialLoad(false);
    }
  }, [statusFilter, debouncedSearchTerm, page, rowsPerPage, enqueueSnackbar]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handlers
  const handleOpenApproveDialog = (request: LeaveRequest) => {
    setApprovingRequest(request);
    setApproveDialogOpen(true);
  };

  const handleApprove = async () => {
    if (!approvingRequest) return;
    
    try {
      await leaveService.approveLeaveRequest(approvingRequest._id);
      enqueueSnackbar('Leave request approved', { variant: 'success' });
      setApproveDialogOpen(false);
      setApprovingRequest(null);
      fetchData();
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || 'Failed to approve', { variant: 'error' });
    }
  };

  const handleOpenRejectDialog = (request: LeaveRequest) => {
    setRejectingRequest(request);
    setRejectionReason('');
    setRejectDialogOpen(true);
  };

  const handleReject = async () => {
    if (!rejectingRequest) return;

    try {
      await leaveService.rejectLeaveRequest(rejectingRequest._id, rejectionReason);
      enqueueSnackbar('Leave request rejected', { variant: 'success' });
      setRejectDialogOpen(false);
      setRejectingRequest(null);
      fetchData();
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || 'Failed to reject', { variant: 'error' });
    }
  };

  // Revoke an approved leave — refunds balance.used on the server side.
  const handleOpenRevokeDialog = (request: LeaveRequest) => {
    setRevokingRequest(request);
    setRevokeDialogOpen(true);
  };
  const handleRevoke = async (revocationReason: string) => {
    if (!revokingRequest) return;
    setRevoking(true);
    try {
      await leaveService.revokeLeaveRequest(revokingRequest._id, revocationReason);
      enqueueSnackbar('Leave revoked and balance refunded', { variant: 'success' });
      setRevokeDialogOpen(false);
      setRevokingRequest(null);
      fetchData();
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || 'Failed to revoke', { variant: 'error' });
    } finally {
      setRevoking(false);
    }
  };

  // Edit an approved leave's dates — server recomputes totalDays and adjusts
  // balance.used by the delta.
  const handleOpenEditDialog = (request: LeaveRequest) => {
    setEditingRequest(request);
    setEditDialogOpen(true);
  };
  const handleEditDates = async (payload: { startDate: string; endDate: string; editReason: string }) => {
    if (!editingRequest) return;
    setEditing(true);
    try {
      await leaveService.editApprovedLeaveDates(editingRequest._id, payload);
      enqueueSnackbar('Leave dates updated', { variant: 'success' });
      setEditDialogOpen(false);
      setEditingRequest(null);
      fetchData();
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || 'Failed to update dates', { variant: 'error' });
    } finally {
      setEditing(false);
    }
  };

  // Count by status
  const pendingCount = leaveRequests.filter(r => r.status === 'PENDING').length;

  // No client-side filtering - server handles it
  const displayedRequests = leaveRequests;

  // Render status chip
  const renderStatusChip = (status: string) => {
    const getStatusColor = (s: string) => {
      switch (s) {
        case 'PENDING': return theme.palette.warning.main;
        case 'APPROVED': return theme.palette.success.main;
        case 'REJECTED': return theme.palette.error.main;
        case 'CANCELLED': return theme.palette.grey[500];
        case 'REVOKED': return theme.palette.warning.dark;
        default: return theme.palette.warning.main;
      }
    };
    const color = getStatusColor(status);
    return (
      <Chip
        label={status}
        size="small"
        sx={{
          bgcolor: alpha(color, 0.1),
          color: color,
          fontWeight: 600,
          fontSize: '0.75rem',
        }}
      />
    );
  };

  // Loading skeleton — only on the very first load
  if (initialLoad && loading) {
    return (
      <Box>
        <Skeleton variant="text" width={250} height={40} sx={{ mb: 2 }} />
        <Skeleton variant="rounded" height={400} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', mb: 3, gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} color="text.primary">
            Leave Approval
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Review and approve employee leave requests
          </Typography>
        </Box>
        
        {pendingCount > 0 && (
          <Chip 
            label={`${pendingCount} pending request${pendingCount > 1 ? 's' : ''}`}
            color="warning"
          />
        )}
      </Box>

      {/* Filters */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 3,
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by employee name, leave type, or reason..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(0); // Reset to first page on search
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(0); // Reset to first page on filter change
                }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="PENDING">Pending</MenuItem>
                <MenuItem value="APPROVED">Approved</MenuItem>
                <MenuItem value="REJECTED">Rejected</MenuItem>
                <MenuItem value="REVOKED">Revoked</MenuItem>
                <MenuItem value="CANCELLED">Cancelled</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Requests Table */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        {!initialLoad && loading && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              bgcolor: alpha(theme.palette.background.paper, 0.6),
              zIndex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          />
        )}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell sx={{ fontWeight: 600 }}>Employee</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Leave Type</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Date Range</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Days</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Reason</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Submitted</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayedRequests.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} sx={{ textAlign: 'center', py: 6 }}>
                    <EventBusy sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                    <Typography variant="body1" color="text.secondary">
                      {searchTerm || statusFilter 
                        ? 'No requests match your filters' 
                        : 'No leave requests found'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                displayedRequests.map((request) => {
                  const employee = typeof request.employeeId === 'object' ? request.employeeId : null;
                  const leaveType = typeof request.leaveTypeId === 'object' ? request.leaveTypeId : null;
                  return (
                    <TableRow key={request._id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Avatar sx={{ width: 32, height: 32, bgcolor: theme.palette.primary.main }}>
                            {employee?.name?.charAt(0) || '?'}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight={500}>
                              {employee?.name || 'Unknown'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {employee?.email || employee?.userCode || ''}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box
                            sx={{
                              width: 10,
                              height: 10,
                              borderRadius: '50%',
                              bgcolor: leaveType?.color || theme.palette.grey[400],
                            }}
                          />
                          <Typography variant="body2">
                            {leaveType?.name || 'Unknown'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {format(new Date(request.startDate), 'MMM dd')}
                          {' - '}
                          {format(new Date(request.endDate), 'MMM dd, yyyy')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={`${request.totalDays} day${request.totalDays > 1 ? 's' : ''}`} size="small" />
                      </TableCell>
                      <TableCell sx={{ maxWidth: 180 }}>
                        <Tooltip title={request.reason}>
                          <Typography variant="body2" noWrap>
                            {request.reason}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        {renderStatusChip(request.status)}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {format(new Date(request.createdAt), 'MMM dd, yyyy')}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        {request.status === 'PENDING' && (
                          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                            <Tooltip title="Approve">
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => handleOpenApproveDialog(request)}
                              >
                                <CheckCircle fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Reject">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleOpenRejectDialog(request)}
                              >
                                <Cancel fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        )}
                        {request.status === 'APPROVED' && (
                          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                            <Tooltip title="Edit dates">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleOpenEditDialog(request)}
                              >
                                <EditCalendarRounded fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Revoke approval">
                              <IconButton
                                size="small"
                                color="warning"
                                onClick={() => handleOpenRevokeDialog(request)}
                              >
                                <Undo fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        )}
                        {request.status === 'REJECTED' && (
                          <Tooltip title={request.rejectionReason || 'No reason provided'}>
                            <Typography variant="caption" color="error.main" sx={{ cursor: 'help' }}>
                              Rejected
                            </Typography>
                          </Tooltip>
                        )}
                        {request.status === 'REVOKED' && (
                          <Tooltip title={request.revocationReason || 'No reason provided'}>
                            <Typography variant="caption" color="warning.dark" sx={{ cursor: 'help' }}>
                              Revoked
                            </Typography>
                          </Tooltip>
                        )}
                        {request.status === 'CANCELLED' && (
                          <Typography variant="caption" color="text.secondary">
                            Cancelled
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25]}
        />
      </Paper>

      {/* Rejection Dialog */}
      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Reject Leave Request
        </DialogTitle>
        <DialogContent>
          {rejectingRequest && (
            <Box sx={{ mb: 2 }}>
              <Card variant="outlined" sx={{ mb: 2 }}>
                <CardContent sx={{ py: 1.5 }}>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="caption" color="text.secondary">Employee</Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {typeof rejectingRequest.employeeId === 'object' 
                          ? rejectingRequest.employeeId.name 
                          : 'Unknown'}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="caption" color="text.secondary">Leave Type</Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {typeof rejectingRequest.leaveTypeId === 'object' 
                          ? rejectingRequest.leaveTypeId.name 
                          : 'Unknown'}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <Typography variant="caption" color="text.secondary">Dates</Typography>
                      <Typography variant="body2">
                        {format(new Date(rejectingRequest.startDate), 'MMM dd, yyyy')}
                        {' - '}
                        {format(new Date(rejectingRequest.endDate), 'MMM dd, yyyy')}
                        {' • '}
                        {rejectingRequest.totalDays} day{rejectingRequest.totalDays > 1 ? 's' : ''}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Box>
          )}
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Rejection Reason (Optional)"
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            placeholder="Provide a reason for rejecting this request..."
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setRejectDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleReject} variant="contained" color="error">
            Reject Request
          </Button>
        </DialogActions>
      </Dialog>

      {/* Approval Confirmation Dialog */}
      <ConfirmationDialog
        open={approveDialogOpen}
        onClose={() => setApproveDialogOpen(false)}
        onConfirm={handleApprove}
        title="Approve Leave Request?"
        message="Are you sure you want to approve this leave request?"
        confirmText="Yes, Approve"
        cancelText="Cancel"
        confirmColor="success"
      >
        {approvingRequest && (
          <Card variant="outlined" sx={{ mt: 2, bgcolor: 'action.hover' }}>
            <CardContent sx={{ py: 1.5 }}>
              <Grid container spacing={1}>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="caption" color="text.secondary">Employee</Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {typeof approvingRequest.employeeId === 'object'
                      ? approvingRequest.employeeId.name
                      : 'Unknown'}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="caption" color="text.secondary">Leave Type</Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {typeof approvingRequest.leaveTypeId === 'object'
                      ? approvingRequest.leaveTypeId.name
                      : 'Unknown'}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Typography variant="caption" color="text.secondary">Dates</Typography>
                  <Typography variant="body2">
                    {format(new Date(approvingRequest.startDate), 'MMM dd, yyyy')}
                    {' - '}
                    {format(new Date(approvingRequest.endDate), 'MMM dd, yyyy')}
                    {' • '}
                    {approvingRequest.totalDays} day{approvingRequest.totalDays > 1 ? 's' : ''}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}
      </ConfirmationDialog>

      {/* Revoke an APPROVED leave — refunds balance.used on the server. */}
      <RevokeLeaveDialog
        open={revokeDialogOpen}
        request={revokingRequest}
        loading={revoking}
        onClose={() => { if (!revoking) { setRevokeDialogOpen(false); setRevokingRequest(null); } }}
        onConfirm={handleRevoke}
      />

      {/* Edit dates of an APPROVED leave — server recomputes totalDays
          and adjusts balance.used by the delta. */}
      <EditLeaveDatesDialog
        open={editDialogOpen}
        request={editingRequest}
        loading={editing}
        onClose={() => { if (!editing) { setEditDialogOpen(false); setEditingRequest(null); } }}
        onConfirm={handleEditDates}
      />
    </Box>
  );
};

export default LeaveApprovalPage;
